/**
 * Canvas helper utilities for the BLVX Canvas Editor.
 */

// Aspect ratio presets
export const ASPECT_RATIOS = [
  { key: '1:1',   label: 'Square',    width: 1080, height: 1080 },
  { key: '9:16',  label: 'Story',     width: 1080, height: 1920 },
  { key: '16:9',  label: 'Landscape', width: 1920, height: 1080 },
  { key: '4:5',   label: 'Portrait',  width: 1080, height: 1350 },
];

// Curated gradient presets for canvas backgrounds
export const GRADIENT_PRESETS = [
  { key: 'midnight',   label: 'Midnight',   css: 'linear-gradient(135deg, #0a0a0a, #1a1a2e, #16213e)' },
  { key: 'ember',      label: 'Ember',      css: 'linear-gradient(135deg, #1a0a00, #4a1a00, #8b3a00)' },
  { key: 'violet',     label: 'Violet',     css: 'linear-gradient(135deg, #1a0030, #3a0060, #6a0dad)' },
  { key: 'ocean',      label: 'Ocean',      css: 'linear-gradient(135deg, #001020, #003060, #0066aa)' },
  { key: 'forest',     label: 'Forest',     css: 'linear-gradient(135deg, #001a00, #003a00, #006a00)' },
  { key: 'gold',       label: 'Gold',       css: 'linear-gradient(135deg, #1a1000, #4a3000, #8b6914)' },
  { key: 'rose',       label: 'Rose',       css: 'linear-gradient(135deg, #1a0010, #4a0030, #8b1450)' },
  { key: 'monochrome', label: 'Mono',       css: 'linear-gradient(135deg, #111111, #333333, #555555)' },
];

// Curated solid color presets
export const SOLID_COLORS = [
  '#000000', '#FFFFFF', '#1a1a2e', '#16213e',
  '#4a1a00', '#8b3a00', '#3a0060', '#6a0dad',
  '#003060', '#006a00', '#8b6914', '#8b1450',
];

/**
 * Calculate canvas display dimensions to fit within a container
 * while maintaining the aspect ratio.
 */
export function fitCanvasToContainer(canvasWidth, canvasHeight, containerWidth, containerHeight) {
  const canvasAspect = canvasWidth / canvasHeight;
  const containerAspect = containerWidth / containerHeight;

  let displayWidth, displayHeight;

  if (canvasAspect > containerAspect) {
    // Canvas is wider — fit to container width
    displayWidth = containerWidth;
    displayHeight = containerWidth / canvasAspect;
  } else {
    // Canvas is taller — fit to container height
    displayHeight = containerHeight;
    displayWidth = containerHeight * canvasAspect;
  }

  // Cap at 75% of container so canvas floats with clear breathing room
  const maxScale = 0.75;
  if (displayWidth > containerWidth * maxScale) {
    displayWidth = containerWidth * maxScale;
    displayHeight = displayWidth / canvasAspect;
  }
  if (displayHeight > containerHeight * maxScale) {
    displayHeight = containerHeight * maxScale;
    displayWidth = displayHeight * canvasAspect;
  }

  const scale = displayWidth / canvasWidth;
  return { displayWidth, displayHeight, scale };
}

/**
 * Parse a CSS linear-gradient string into Fabric.js gradient stops.
 * Returns { colorStops, coords } for use with fabric.Gradient.
 */
export function parseGradientToFabric(cssGradient, width, height) {
  // Extract colors from "linear-gradient(135deg, #color1, #color2, #color3)"
  const match = cssGradient.match(/linear-gradient\((\d+)deg,\s*(.+)\)/);
  if (!match) return null;

  const angle = parseInt(match[1], 10);
  const rawColors = match[2].split(',').map(c => c.trim());

  // Parse color stops — CSS allows "color percentage" e.g. "#FFF 50%"
  const colorStops = rawColors.map((raw, i) => {
    const parts = raw.split(/\s+/);
    const color = parts[0];
    const offset = parts[1]
      ? parseFloat(parts[1]) / 100
      : i / (rawColors.length - 1);
    return { offset, color };
  });

  // Convert angle to coordinates
  const rad = (angle - 90) * (Math.PI / 180);
  const coords = {
    x1: width / 2 - (Math.cos(rad) * width) / 2,
    y1: height / 2 - (Math.sin(rad) * height) / 2,
    x2: width / 2 + (Math.cos(rad) * width) / 2,
    y2: height / 2 + (Math.sin(rad) * height) / 2,
  };

  return { colorStops, coords };
}
